import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { subscriptionServiceZxsem, SubscriptionResponse } from '../services/subscriptionServiceZxsem'
import AnalyticsService from '../services/analyticsService'
import AuditService from '../services/auditService'
import hotjarService from '../services/hotjarService'
import { trackSignupConversion, trackSignupConversionIfNewAccount } from '../services/googleAdsService'

// Interface pour le cache d'abonnement
interface SubscriptionCache {
  data: SubscriptionResponse
  timestamp: number
  email: string
}

const SUBSCRIPTION_CACHE_KEY = 'parking_subscription_cache'
const CACHE_DURATION = 30 * 60 * 1000 // 30 minutes en millisecondes

// Fonctions utilitaires pour le cache
const getCachedSubscription = (email: string): SubscriptionResponse | null => {
  try {
    const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
    if (!cached) return null

    const cache: SubscriptionCache = JSON.parse(cached)

    // Vérifier si le cache est pour le bon email et n'est pas expiré
    if (cache.email !== email) return null
    if (Date.now() - cache.timestamp > CACHE_DURATION) {
      localStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
      return null
    }

    console.log('📋 Abonnement trouvé en cache:', cache.data)
    return cache.data
  } catch (error) {
    console.warn('Erreur lecture cache abonnement:', error)
    return null
  }
}

const setCachedSubscription = (email: string, subscription: SubscriptionResponse): void => {
  try {
    const cache: SubscriptionCache = {
      data: subscription,
      timestamp: Date.now(),
      email: email
    }
    localStorage.setItem(SUBSCRIPTION_CACHE_KEY, JSON.stringify(cache))
    console.log('💾 Abonnement mis en cache:', subscription)
  } catch (error) {
    console.warn('Erreur écriture cache abonnement:', error)
  }
}

const clearSubscriptionCache = (): void => {
  try {
    localStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
    console.log('🗑️ Cache abonnement vidé')
  } catch (error) {
    console.warn('Erreur suppression cache abonnement:', error)
  }
}

// Retry une seule fois sur erreur réseau transitoire ("Failed to fetch", etc.)
// Les erreurs métier (email pris, mot de passe invalide…) ne sont PAS retentées.
async function retryOnNetworkError<T>(fn: () => Promise<T>, retries = 1): Promise<T> {
  try {
    return await fn()
  } catch (err: any) {
    const message = err?.message || ''
    const isNetworkError =
      err instanceof TypeError &&
      /failed to fetch|networkerror|load failed/i.test(message)
    if (retries > 0 && isNetworkError) {
      console.warn('🔁 Erreur réseau transitoire, nouvelle tentative dans 500ms:', message)
      await new Promise(resolve => setTimeout(resolve, 500))
      return retryOnNetworkError(fn, retries - 1)
    }
    throw err
  }
}

interface AuthContextType {
  user: User | null
  session: Session | null
  subscription: SubscriptionResponse | null
  loading: boolean
  signUp: (email: string, password: string, fullName?: string, src?: string, eventId?: string) => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signOut: () => Promise<void>
  checkSubscription: (forceRefresh?: boolean) => Promise<void>
  hasActiveSubscription: () => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const isCheckingRef = useRef(false)

  // Initialiser subscription avec le cache si disponible
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(() => {
    try {
      const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
      if (cached) {
        const cache: SubscriptionCache = JSON.parse(cached)
        if (Date.now() - cache.timestamp <= CACHE_DURATION) {
          console.log('🚀 Initialisation avec cache d\'abonnement:', cache.data)
          return cache.data
        } else {
          console.log('⏰ Cache d\'abonnement expiré, suppression')
          localStorage.removeItem(SUBSCRIPTION_CACHE_KEY)
        }
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de l\'initialisation du cache:', error)
    }
    return null
  })

  // Initialiser loading à false si on a un cache valide d'abonnement actif
  const [loading, setLoading] = useState(() => {
    try {
      const cached = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
      if (cached) {
        const cache: SubscriptionCache = JSON.parse(cached)
        if (Date.now() - cache.timestamp <= CACHE_DURATION &&
            (cache.data.status === 'active' || cache.data.status === 'trial')) {
          console.log('⚡ Cache valide trouvé, démarrage sans loading')
          return false
        }
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors de la vérification du cache pour loading:', error)
    }
    return true
  })

  const checkSubscription = useCallback(async (forceRefresh: boolean = false, emailOverride?: string) => {
    const emailToCheck = emailOverride || user?.email

    if (!emailToCheck) {
      console.log('🔍 No user email, skipping subscription check')
      const currentCache = localStorage.getItem(SUBSCRIPTION_CACHE_KEY)
      if (!currentCache) {
        setSubscription(null)
      } else {
        console.log('💾 Cache existant préservé malgré l\'absence temporaire d\'email')
      }
      setLoading(false)
      return
    }

    // Protection contre les appels concurrents
    if (isCheckingRef.current) {
      console.log('⚠️ Vérification déjà en cours, ignorée')
      return
    }
    isCheckingRef.current = true

    // 1. Vérifier le cache d'abord (sauf si forceRefresh)
    if (!forceRefresh) {
      const cachedSubscription = getCachedSubscription(emailToCheck)
      if (cachedSubscription && (cachedSubscription.status === 'active' || cachedSubscription.status === 'trial')) {
        console.log('✅ Abonnement actif trouvé en cache, utilisation immédiate')
        setSubscription(cachedSubscription)
        setLoading(false)
        isCheckingRef.current = false

        // Vérifier en arrière-plan toutes les 5 minutes pour détecter les annulations
        const lastCheck = sessionStorage.getItem('last_subscription_check')
        const now = Date.now()
        if (!lastCheck || now - parseInt(lastCheck) > 5 * 60 * 1000) {
          console.log('🔄 Vérification en arrière-plan pour détecter les changements...')
          sessionStorage.setItem('last_subscription_check', now.toString())
          setTimeout(() => checkSubscription(true, emailToCheck), 100)
        }
        return
      }
    }

    // 2. Appel API unique via le service Xsem (passerelle SystemPayment)
    let finalSubscription: SubscriptionResponse | null = null
    let hasValidSubscription = false

    try {
      console.log('🔍 Vérification abonnement API pour:', emailToCheck)
      const subscriptionData = await subscriptionServiceZxsem.checkSubscriptionStatus(emailToCheck)

      if (subscriptionData && (subscriptionData.status === 'active' || subscriptionData.status === 'trial')) {
        console.log('✅ Abonnement actif trouvé via API:', subscriptionData)
        finalSubscription = subscriptionData
        hasValidSubscription = true
      } else if (subscriptionData) {
        // cancelled / expired / suspended → pas d'abonnement actif
        console.log('🚫 Abonnement trouvé mais statut non actif:', subscriptionData.status)
        finalSubscription = null
      } else {
        // API muette → essayer le cache en fallback
        const cachedSubscription = getCachedSubscription(emailToCheck)
        if (cachedSubscription && (cachedSubscription.status === 'active' || cachedSubscription.status === 'trial')) {
          console.log('⚠️ API muette, utilisation du cache comme fallback')
          finalSubscription = cachedSubscription
          hasValidSubscription = true
        } else {
          console.log('ℹ️ Aucun abonnement trouvé (API + cache)')
          finalSubscription = null
        }
      }
    } catch (error) {
      console.error('❌ Erreur vérification abonnement:', error)
      // Cache d'urgence en cas d'erreur réseau
      const cachedSubscription = getCachedSubscription(emailToCheck)
      if (cachedSubscription && (cachedSubscription.status === 'active' || cachedSubscription.status === 'trial')) {
        console.log('🚨 Erreur API, utilisation du cache d\'urgence')
        finalSubscription = cachedSubscription
        hasValidSubscription = true
      } else {
        console.log('🚨 Erreur API et pas de cache valide')
        finalSubscription = null
      }
    }

    // 3. Mettre à jour l'état et le cache
    setSubscription(finalSubscription)

    if (finalSubscription && hasValidSubscription) {
      setCachedSubscription(emailToCheck, finalSubscription)
    } else {
      console.log('🗑️ Aucun abonnement actif, vidage du cache')
      clearSubscriptionCache()
    }

    setLoading(false)
    isCheckingRef.current = false
  }, [user?.email])

  useEffect(() => {
    let mounted = true

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return

      setSession(session)
      setUser(session?.user ?? null)

      // Initialiser Hotjar pour la session initiale si utilisateur connecté
      if (session?.user?.email && session?.user?.id) {
        hotjarService.initializeForUser(
          session.user.email,
          session.user.id,
          {
            full_name: session.user.user_metadata?.full_name,
            created_at: session.user.created_at
          }
        ).catch(error => {
          console.error('❌ Erreur lors de l\'initialisation Hotjar (session initiale):', error)
        })
      }

      if (session?.user?.email) {
        // Vérifier si on a déjà un abonnement en cache pour cet utilisateur
        const cachedSub = getCachedSubscription(session.user.email)
        if (cachedSub && (cachedSub.status === 'active' || cachedSub.status === 'trial')) {
          console.log('✅ Cache valide trouvé pour', session.user.email, '- pas besoin de recharger immédiatement')
          if (mounted) {
            setLoading(false)
          }
          // Vérifier en arrière-plan pour mettre à jour si nécessaire
          checkSubscription(false, session.user.email).catch(error => {
            console.error('❌ Erreur lors de la vérification en arrière-plan:', error)
          })
        } else {
          try {
            console.log('🔍 Initial subscription check for:', session.user.email)
            await checkSubscription(false, session.user.email)
          } catch (error) {
            console.error('❌ Error checking initial subscription:', error)
            if (mounted) {
              setSubscription(null)
            }
          }
          if (mounted) {
            setLoading(false)
          }
        }
      } else {
        setSubscription(null)
        if (mounted) {
          setLoading(false)
        }
      }
    })

    // Listen for auth changes
    const {
      data: { subscription: authSubscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      console.log('🔄 Auth state changed:', event)

      // Vider le cache lors de la déconnexion
      if (event === 'SIGNED_OUT') {
        clearSubscriptionCache()
      }

      setSession(session)
      setUser(session?.user ?? null)

      // Associer l'utilisateur à Google Analytics
      if (session?.user?.id) {
        AnalyticsService.setUserId(session.user.id)
      }

      // Initialiser Hotjar pour l'utilisateur connecté
      if (session?.user?.email && session?.user?.id) {
        hotjarService.initializeForUser(
          session.user.email,
          session.user.id,
          {
            full_name: session.user.user_metadata?.full_name,
            created_at: session.user.created_at
          }
        ).catch(error => {
          console.error('❌ Erreur lors de l\'initialisation Hotjar:', error)
        })
      } else if (!session?.user) {
        hotjarService.resetUser()
      }

      // Postback inscription OAuth (Google)
      if (event === 'SIGNED_IN' && session?.user?.app_metadata?.provider === 'google') {
        const oauthEventId = sessionStorage.getItem('oauth_event_id')
        if (oauthEventId) {
          const PostbackService = (await import('../services/postbackService')).default
          PostbackService.sendRegistrationPostback(oauthEventId)
          sessionStorage.setItem('payment_event_id', oauthEventId)
          sessionStorage.removeItem('oauth_event_id')
          console.log('📤 Postback OAuth envoyé avec event_id:', oauthEventId)
        }
        // Conversion Google Ads : uniquement si le compte Google vient d'être créé
        trackSignupConversionIfNewAccount(session.user.created_at)
      }

      if (session?.user?.email) {
        try {
          console.log('🔄 Auth change subscription check for:', session.user.email)
          await checkSubscription(false, session.user.email)
        } catch (error) {
          console.error('❌ Error checking subscription after auth change:', error)
          if (mounted) {
            setSubscription(null)
          }
        }
      } else {
        setSubscription(null)
      }

      if (mounted) {
        setLoading(false)
      }
    })

    return () => {
      mounted = false
      authSubscription.unsubscribe()
    }
  }, []) // Pas de dépendances pour éviter la boucle

  const signUp = async (email: string, password: string, fullName?: string, src?: string, eventId?: string) => {
    setLoading(true)
    try {
      const { error } = await retryOnNetworkError(() => supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            src: src,
            event_id: eventId,
          },
        },
      }))
      if (error) throw error

      if (eventId) {
        const PostbackService = (await import('../services/postbackService')).default
        PostbackService.sendRegistrationPostback(eventId)
        sessionStorage.setItem('payment_event_id', eventId)
      }

      AnalyticsService.trackSignUp('email')
      // Conversion Google Ads : nouvelle inscription par email
      trackSignupConversion()
    } catch (error) {
      AnalyticsService.trackError('signup_error', error instanceof Error ? error.message : 'Unknown signup error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (email: string, password: string) => {
    setLoading(true)
    try {
      const { error } = await retryOnNetworkError(() => supabase.auth.signInWithPassword({
        email,
        password,
      }))
      if (error) throw error

      AnalyticsService.trackLogin('email')

      const { data: { user } } = await supabase.auth.getUser()
      if (user?.id) {
        await AuditService.logUserActivity({
          userId: user.id,
          eventType: 'USER_SIGNIN',
          details: { method: 'email', email }
        })
      }
      // Note: On ne met pas setLoading(false) ici car on veut attendre que
      // onAuthStateChange -> checkSubscription termine la vérification
      // pour éviter une redirection prématurée vers /subscription
    } catch (error) {
      AnalyticsService.trackError('signin_error', error instanceof Error ? error.message : 'Unknown signin error')
      setLoading(false)
      throw error
    }
  }

  const signInWithGoogle = async () => {
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/auth',
        },
      })
      if (error) throw error

      AnalyticsService.trackSignUp('google')
    } catch (error) {
      AnalyticsService.trackError('google_signin_error', error instanceof Error ? error.message : 'Unknown Google signin error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const signOut = async () => {
    // Log audit pour déconnexion avant de déconnecter
    if (user?.id) {
      await AuditService.logUserActivity({
        userId: user.id,
        eventType: 'USER_SIGNOUT',
        details: { email: user.email }
      })
    }

    setLoading(true)
    try {
      const { error } = await supabase.auth.signOut()
      if (error) throw error
      setSubscription(null)

      // Vider le cache d'abonnement lors de la déconnexion
      clearSubscriptionCache()

      AnalyticsService.trackEvent('logout', {
        event_category: 'authentication',
      })
    } catch (error) {
      AnalyticsService.trackError('signout_error', error instanceof Error ? error.message : 'Unknown signout error')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const hasActiveSubscription = (): boolean => {
    return subscription?.status === 'active' || subscription?.status === 'trial'
  }

  const value: AuthContextType = {
    user,
    session,
    subscription,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    checkSubscription,
    hasActiveSubscription,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
